<script lang="ts">
	import type { Game, GameType } from '../lib/catalog'
	import GameCard from './GameCard.svelte'

	let {
		games,
		total,
		counts,
		tab = $bindable(),
		selected,
		query = $bindable(),
		onpick,
	}: {
		games: readonly Game[]
		/** Titles of the active type, before the search narrows them. */
		total: number
		counts: Readonly<Record<GameType, number>>
		tab: GameType
		selected: string
		query: string
		onpick: (slug: string) => void
	} = $props()

	const TABS: ReadonlyArray<{ key: GameType; label: string }> = [
		{ key: 'game', label: 'GAMES' },
		{ key: 'prototype', label: 'PROTOTYPES' },
	]
</script>

<main>
	<div class="topbar">
		<!--
			Pressed toggles rather than role="tablist": a real tab pattern owes the
			keyboard arrow-key navigation and the grid an id to be controlled by,
			and claiming the role without either is worse than not claiming it.
		-->
		<div class="tabs" role="group" aria-label="Catalogue section">
			{#each TABS as t (t.key)}
				<button
					class="tab"
					class:active={t.key === tab}
					type="button"
					aria-pressed={t.key === tab}
					onclick={() => (tab = t.key)}
				>
					<span>{t.label}</span>
					<span class="tabcount">{counts[t.key]}</span>
				</button>
			{/each}
		</div>

		<input
			bind:value={query}
			type="text"
			placeholder="search game by name…"
			aria-label="Search games"
			spellcheck="false"
		/>
		<span class="count">{games.length} / {total} TITLES</span>
	</div>

	<div class="grid-scroll">
		<div class="grid">
			{#each games as game (game.slug)}
				<GameCard {game} selected={game.slug === selected} {onpick} />
			{/each}
		</div>

		{#if games.length === 0}
			<!--
				An empty prototypes section is the normal starting state, not a
				failed search — saying "no match" for a query nobody typed would
				send a tester looking for a filter to clear.
			-->
			<p class="empty">
				{#if query.trim()}
					no {tab === 'prototype' ? 'prototype' : 'game'} matches “{query}”
				{:else if tab === 'prototype'}
					no prototypes published yet
				{:else}
					no games yet — add one in the back office
				{/if}
			</p>
		{/if}
	</div>
</main>

<style>
	main {
		display: flex;
		flex-direction: column;
		flex: 1;
		min-width: 0;
	}

	.topbar {
		display: flex;
		align-items: center;
		gap: 16px;
		padding: 18px 26px;
		border-bottom: 1px solid var(--line);
	}

	/* A segmented control: one box holding both tabs, the active one filled. */
	.tabs {
		display: flex;
		flex: none;
		gap: 3px;
		padding: 3px;
		border: 1px solid var(--line-strong);
		border-radius: 7px;
		background: var(--panel);
	}

	.tab {
		display: flex;
		align-items: center;
		gap: 7px;
		padding: 7px 13px;
		border: none;
		border-radius: 5px;
		background: transparent;
		color: var(--muted);
		font: inherit;
		font-size: 11px;
		letter-spacing: 0.08em;
		cursor: pointer;
	}

	.tab:hover {
		color: var(--text);
	}

	.tab.active {
		background: var(--raised);
		color: var(--text);
	}

	.tabcount {
		color: var(--dim);
		font-size: 10px;
	}

	.topbar input {
		flex: 1;
		min-width: 0;
		padding: 10px 13px;
		border: 1px solid var(--line-strong);
		border-radius: var(--radius);
		background: var(--panel);
		color: var(--text);
		font-size: 13px;
	}

	.topbar input::placeholder {
		color: var(--faint);
	}

	.count {
		color: var(--dim);
		font-size: 11px;
		letter-spacing: 0.06em;
		white-space: nowrap;
	}

	.grid-scroll {
		flex: 1;
		padding: 24px 26px;
		overflow: auto;
	}

	/*
		The design draws four fixed columns at its 1320px frame. auto-fill against
		that same 940px content box still lands on four, and keeps the card size
		honest on the wider monitors this actually runs on.
	*/
	.grid {
		display: grid;
		grid-template-columns: repeat(auto-fill, minmax(210px, 1fr));
		gap: 16px;
	}

	.empty {
		margin: 0;
		padding: 60px 0;
		color: var(--faint);
		font-size: 12px;
		text-align: center;
	}
</style>
